"use client";

import { useState, useCallback, useEffect, useMemo } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { AutocompleteSearchBar } from "@/components/search/autocomplete-search-bar";
import { PageHeroWithSearch } from "@/components/sections/page-hero-with-search";
import { CategoryPills } from "@/components/category-pills";
import { PageBackground } from "@/components/sections/page-background";
import { ScrollTile } from "@/components/scroll-tile";
import { TileGrid, TileGridSkeleton } from "@/components/tile-grid";
import { SimplePagination } from "@/components/pagination/simple-pagination";
import { useShops, ALL_SHOPS_SENTINEL } from "@/app/admin/shops/_api/use-shop-queries";
import { useMainCategoriesWithCounts } from "@/hooks/queries";
import { POPULAR_SHOP_CATEGORY_ID } from "@/lib/constants";

const SHOPS_PER_PAGE = 29;

export function ShopsPageContent() {
  const t = useTranslations("shops");
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const initialSearch = searchParams.get("search") ?? "";
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const selectedCategory =
    searchParams.get("category") ?? ALL_SHOPS_SENTINEL;
  const pageParam = searchParams.get("page");
  const currentPage = Math.max(1, pageParam ? parseInt(pageParam, 10) || 1 : 1);

  const handleSubmit = useCallback(
    (value: string) => {
      setSearchQuery(value);
      const params = new URLSearchParams();
      if (value) params.set("search", value);
      if (selectedCategory !== ALL_SHOPS_SENTINEL)
        params.set("category", selectedCategory);
      params.set("page", "1");
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [selectedCategory, router, pathname],
  );

  const handleCategorySelect = useCallback(
    (categoryId: string) => {
      const params = new URLSearchParams();
      if (searchQuery) params.set("search", searchQuery);
      if (categoryId !== ALL_SHOPS_SENTINEL)
        params.set("category", categoryId);
      params.set("page", "1");
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [searchQuery, router, pathname],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const params = new URLSearchParams();
      if (searchQuery) params.set("search", searchQuery);
      if (selectedCategory !== ALL_SHOPS_SENTINEL)
        params.set("category", selectedCategory);
      if (page > 1) params.set("page", String(page));
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [searchQuery, selectedCategory, router, pathname],
  );

  // Persist return URL for breadcrumb navigation
  useEffect(() => {
    sessionStorage.setItem("donista.shopsReturnUrl", window.location.href);
  }, [searchQuery, selectedCategory, currentPage]);

  // Categories data
  const { data: categoriesData } = useMainCategoriesWithCounts();

  const categoryItems = useMemo(
    () => [
      { id: ALL_SHOPS_SENTINEL, label: t("categories.allShops"), icon: "shopping-bag" },
      ...(categoriesData?.categories ?? []).map((cat) => ({ id: cat.id, label: cat.name, icon: cat.icon, color: cat.color })),
    ],
    [categoriesData?.categories, t],
  );

  // Shops data
  const { data: shopsPage, isLoading } = useShops({
    categoryId: selectedCategory,
    searchTerm: searchQuery || undefined,
    page: currentPage - 1,
    size: SHOPS_PER_PAGE,
    isPopular: selectedCategory === POPULAR_SHOP_CATEGORY_ID,
  });

  const shops = shopsPage?.content ?? [];
  const totalPages = shopsPage?.totalPages ?? 0;

  return (
    <PageBackground>
      <PageHeroWithSearch
        title={t("page.hero.headline")}
        subtitle={t("page.hero.subtitle")}
      >
        <AutocompleteSearchBar
          sources={["shops"]}
          onSubmit={handleSubmit}
          defaultValue={initialSearch}
          placeholder={t("page.hero.searchPlaceholder")}
        />
      </PageHeroWithSearch>

      {/* Categories */}
      <section className="py-4">
        <div className="max-w-7xl mx-auto">
          <CategoryPills
            items={categoryItems}
            activeId={selectedCategory}
            onSelect={handleCategorySelect}
          />
        </div>
      </section>

      {/* Grid */}
      <div className="max-w-7xl mx-auto space-y-6 pb-8">
        {isLoading ? (
          <TileGridSkeleton count={12}  />
        ) : shops.length > 0 ? (
          <TileGrid >
            <ScrollTile
              href="/shops/amazon"
              name="Amazon"
              logoUrl="/start/amazon-logo.png"
              className="bg-[#FF9900]/10"
            />
            {shops.map((shop) => (
              <ScrollTile
                key={shop.id}
                href={`/shops/${shop.slug}`}
                name={shop.name ?? ""}
                logoUrl={shop.logoUrl}
                shopId={shop.id}
                commissionRangeSummary={shop.commissionRangeSummary}
              />
            ))}
          </TileGrid>
        ) : (
          <div className="text-center py-10">
            <p className="text-muted-foreground">{t("page.noShops")}</p>
          </div>
        )}

        <SimplePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </PageBackground>
  );
}
